import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Users, Target, Award, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const About = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: Users,
      title: 'Customer First',
      description: 'We prioritize our customers in every decision we make, ensuring their satisfaction and trust.'
    },
    {
      icon: Target,
      title: 'Quality Service',
      description: 'We maintain the highest standards in service delivery with trained and verified professionals.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in every interaction, continuously improving our services.'
    },
    {
      icon: Heart,
      title: 'Care & Compassion',
      description: 'We treat every home as our own, providing services with genuine care and attention.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_ec527428-3aec-42a4-a1f7-d2e66c0be0df/artifacts/hktez0bk_logo_transs.png" 
                alt="doorush logo" 
                className="h-10 w-auto"
              />
            </div>
            <Button onClick={() => navigate('/')} variant="outline" className="border-2 border-[#007e7a] text-[#007e7a] hover:bg-[#007e7a] hover:text-white">
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007e7a]/5 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            About Us
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Welcome to doorush – Your On-Demand Home Help & Cooking Partner!
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Introduction */}
          <div>
            <p className="text-lg text-gray-600 leading-relaxed">
              At doorush, operated by <strong>Holbewoner Technologies Private Limited</strong>, we believe that everyday help at home should be instant, reliable, and effortless. Our mission is to connect you with trained professionals who can assist with cooking, cleaning, dishwashing, laundry, and more — all within just <strong>10 minutes</strong> of booking.
            </p>
          </div>

          {/* Our Story */}
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                doorush was born from a simple idea — what if getting help at home could be as easy and fast as ordering food?
              </p>
              <p>
                From last-minute maid cancellations to unplanned cooking stress, we've all been there. That's why we built a platform where you can instantly book "Quick Help" for daily chores or an "Insta Cook" to whip up fresh meals right in your kitchen.
              </p>
              <p>
                Today, we're redefining how households access essential help — with trained professionals, transparent pricing, and doorstep convenience.
              </p>
            </div>
          </div>

          {/* Why Choose doorush */}
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Choose doorush?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="text-2xl">🕒</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">10-Minute Arrival</h3>
                  <p className="text-gray-600">Book now, get help in just minutes.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="text-2xl">👩‍🍳</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Skilled & Verified Partners</h3>
                  <p className="text-gray-600">Background-checked professionals for cooking and household tasks.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="text-2xl">📱</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Seamless App Experience</h3>
                  <p className="text-gray-600">Easy booking, live tracking, and secure payments.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="text-2xl">💰</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Transparent Pricing</h3>
                  <p className="text-gray-600">Clear, fair rates. Pay per task or per hour — no hidden charges.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="text-2xl">🧼</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Safe & Respectful Service</h3>
                  <p className="text-gray-600">Our partners follow professional conduct and safety standards.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="text-2xl">📞</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Dedicated Support</h3>
                  <p className="text-gray-600">Reach us easily for any assistance or rescheduling.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Vision & Mission */}
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-6 bg-gradient-to-br from-[#007e7a]/5 to-white border-2 border-[#007e7a]/10">
              <CardContent className="pt-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h2>
                <p className="text-gray-600 leading-relaxed">
                  To become India's most trusted and fastest platform for on-demand household services — from kitchen help to daily chores — making everyday living easier, safer, and smarter.
                </p>
              </CardContent>
            </Card>
            <Card className="p-6 bg-gradient-to-br from-[#007e7a]/5 to-white border-2 border-[#007e7a]/10">
              <CardContent className="pt-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
                <ul className="space-y-2 text-gray-600">
                  <li>• Empower households with instant access to trusted home helpers and cooks.</li>
                  <li>• Create dignified, flexible job opportunities for skilled service workers.</li>
                  <li>• Use technology to simplify daily life, one task at a time.</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Join Community */}
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Join the doorush Community</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              doorush is more than just a service — it's a movement to make home life stress-free, starting with the help you need, exactly when you need it. Whether you're a busy professional, a parent, or someone living alone — we're here to back you up.
            </p>
          </div>

          {/* Contact Info */}
          <div className="bg-gray-50 rounded-3xl p-8 border-2 border-gray-100">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Get in Touch</h2>
            <div className="space-y-3 text-gray-600">
              <p className="text-xl font-bold text-gray-900">Holbewoner Technologies Private Limited</p>
              <p>🌐 Website: <a href="https://www.doorush.in" className="text-[#007e7a] hover:underline" target="_blank" rel="noopener noreferrer">www.doorush.in</a></p>
              <p>📧 Email: <a href="mailto:support@doorush.in" className="text-[#007e7a] hover:underline">support@doorush.in</a></p>
              <p>📍 Address: 1st floor, 1/2A, 12-B, 1st Main Road, Sowmya Nagar, Perumbakkam, Chennai - 600100</p>
            </div>
          </div>

          {/* Tagline */}
          <div className="text-center py-8">
            <p className="text-2xl font-bold text-[#007e7a] italic">
              "doorush — Help at Your Step, in Just Minutes."
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="text-center pt-6">
                    <div className="w-16 h-16 bg-[#007e7a]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-[#007e7a]" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#007e7a] to-[#005555] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Join the doorush Family</h2>
          <p className="text-xl mb-8 opacity-90">
            Experience the future of home services today
          </p>
          <Button 
            onClick={() => navigate('/')}
            size="lg" 
            className="bg-white text-[#007e7a] hover:bg-gray-100 text-lg px-8 py-6"
          >
            Get Started Now
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;